<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import { routes } from '@/router'
import AppIcon from '@/components/ui/AppIcon.vue'
import { useRefundQueueStore } from '@/stores/refunds'
import { useApplicationQueueStore } from '@/stores/segmentApplications'
import { useSessionStore } from '@/stores/session'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const route = useRoute()
const session = useSessionStore()
const refundQueue = useRefundQueueStore()
const applicationQueue = useApplicationQueueStore()

interface NavItem {
  name: string
  titleKey: string
  icon: string
  badge?: number
}

/**
 * The menu is derived from the routes, filtered by what this user may open, so
 * nothing in it leads to a permission error.
 */
const groups = computed(() => {
  const order = ['Operations', 'Customers', 'Marketing', 'Insight', 'Configuration'] as const
  const collected = new Map<string, NavItem[]>()
  const top: NavItem[] = []

  for (const record of routes) {
    if (record.meta?.hidden || !record.name || !record.meta?.titleKey) continue

    const required = record.meta.permissions
    if (required && required.length > 0 && !session.canAny(...required)) continue

    const item: NavItem = {
      name: String(record.name),
      titleKey: record.meta.titleKey,
      icon: record.meta.icon ?? 'info',
    }
    if (record.name === 'refunds' && refundQueue.pending > 0) {
      item.badge = refundQueue.pending
    }
    if (record.name === 'segment-applications' && applicationQueue.pending > 0) {
      item.badge = applicationQueue.pending
    }

    if (!record.meta.group) {
      top.push(item)
    } else {
      const bucket = collected.get(record.meta.group) ?? []
      bucket.push(item)
      collected.set(record.meta.group, bucket)
    }
  }

  return {
    top,
    groups: order
      .filter((name) => (collected.get(name)?.length ?? 0) > 0)
      .map((name) => ({ name, items: collected.get(name)! })),
  }
})

function isActive(name: string): boolean {
  return route.name === name
}
</script>

<template>
  <div v-if="props.open" class="scrim" @click="emit('close')" />

  <aside class="sidebar" :class="props.open && 'sidebar--open'">
    <div class="sidebar__brand">
      <span class="sidebar__mark" aria-hidden="true">
        <svg viewBox="0 0 32 32" width="22" height="22">
          <rect width="32" height="32" rx="8" fill="var(--brand-500)" />
          <path
            d="M16 7.5l2.6 5.6 6.1.8-4.5 4.2 1.2 6L16 21.2l-5.4 2.9 1.2-6-4.5-4.2 6.1-.8z"
            fill="#fff"
          />
        </svg>
      </span>
      <span class="sidebar__name">{{ t('header.brand') }}</span>
    </div>

    <nav class="sidebar__nav" :aria-label="t('header.mainNav')">
      <RouterLink
        v-for="item in groups.top"
        :key="item.name"
        class="nav__link"
        :class="isActive(item.name) && 'nav__link--active'"
        :to="{ name: item.name }"
        @click="emit('close')"
      >
        <AppIcon :name="item.icon" :size="15" />
        <span>{{ t(item.titleKey) }}</span>
      </RouterLink>

      <div v-for="group in groups.groups" :key="group.name" class="nav__group">
        <p class="nav__heading">{{ t(`groups.${group.name}`) }}</p>
        <RouterLink
          v-for="item in group.items"
          :key="item.name"
          class="nav__link"
          :class="isActive(item.name) && 'nav__link--active'"
          :to="{ name: item.name }"
          @click="emit('close')"
        >
          <AppIcon :name="item.icon" :size="15" />
          <span>{{ t(item.titleKey) }}</span>
          <span v-if="item.badge" class="nav__badge">{{ item.badge }}</span>
        </RouterLink>
      </div>
    </nav>
  </aside>
</template>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  flex: none;
  width: var(--sidebar-width);
  background: var(--surface);
  border-inline-end: 1px solid var(--border);
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
}

.sidebar__brand {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  height: var(--header-height);
  padding: 0 1rem;
  border-bottom: 1px solid var(--border);
  flex: none;
}

.sidebar__mark { display: grid; place-items: center; }
.sidebar__name { font-size: 0.9375rem; font-weight: 600; letter-spacing: -0.01em; }

.sidebar__nav { padding: 0.75rem 0.6rem 1.5rem; }

.nav__group { margin-top: 1.05rem; }

.nav__heading {
  margin: 0 0 0.3rem;
  padding: 0 0.6rem;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--text-subtle);
}

.nav__link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.48rem 0.6rem;
  margin-bottom: 1px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-muted);
  border-radius: var(--radius);
  text-decoration: none;
  transition: background 110ms ease, color 110ms ease;
}

.nav__link:hover { background: var(--surface-hover); color: var(--text); text-decoration: none; }

.nav__link--active {
  color: var(--brand-500);
  background: var(--brand-50);
}

.nav__badge {
  margin-inline-start: auto;
  min-width: 18px;
  padding: 0 0.32rem;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  color: var(--text-inverse);
  background: #c0303a;
  border-radius: 999px;
}

.scrim {
  position: fixed;
  inset: 0;
  z-index: 29;
  background: rgb(10 14 20 / 45%);
}

@media (max-width: 900px) {
  .sidebar {
    position: fixed;
    inset-block: 0;
    inset-inline: 0 auto;
    z-index: 30;
    transform: translateX(-100%);
    transition: transform 180ms ease;
    box-shadow: var(--shadow-lg);
  }

  /* Off-canvas towards the edge it lives on, which swaps under Arabic. */
  [dir='rtl'] .sidebar { transform: translateX(100%); }

  .sidebar--open,
  [dir='rtl'] .sidebar--open { transform: translateX(0); }
}
</style>
